import { Stack, Typography } from '@mui/material';
import { useCallback } from 'react';

import { SubpageLayout } from '@/components/architecture/Layout/SubpageLayout';
import { Button } from '@/components/form/baseInputs/Button';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { useNavigateToRoute } from '@/components/navigation/useNavigateToRoute';
import { DataTableCard } from '@/components/tables/DataTable/DataTableCard';
import { NEW_TEMPLATE_SENTINEL } from '@/modules/presentationTemplates/PresentationTemplateEditor/PresentationTemplateEditor.constants';
import { PresentationTemplateTableVariant } from '@/modules/presentationTemplates/presentationTemplateTable.types';
import { SystemPresentationTemplateTable } from '@/modules/presentationTemplates/systemPresentationTemplateTable/SystemPresentationTemplateTable';
import { TenantPresentationTemplateTable } from '@/modules/presentationTemplates/tenantPresentationTemplateTable/TenantPresentationTemplateTable';
import { ROUTE_KEYS } from '@/navigation/constants';

export function AdminPresentationTemplatesPage() {
  const { navigate } = useNavigateToRoute();

  const openEditor = useCallback(
    (templateId: string) => {
      navigate(ROUTE_KEYS.ADMIN_PRESENTATION_TEMPLATE_EDITOR, { templateId });
    },
    [navigate]
  );

  // System templates are read-only, so clicking one opens the editor in
  // save-as-new mode (forcing a new template name) rather than editing in place.
  const openSystemTemplateAsNew = useCallback(
    (templateId: string) => {
      navigate(
        ROUTE_KEYS.ADMIN_PRESENTATION_TEMPLATE_EDITOR,
        { templateId },
        { isDuplicateFlow: 'true' }
      );
    },
    [navigate]
  );

  const onClickCreate = useCallback(() => {
    openEditor(NEW_TEMPLATE_SENTINEL);
  }, [openEditor]);

  return (
    <SubpageLayout
      heading="Presentation templates"
      actions={
        <Button
          size="sm"
          variant="secondary"
          startIcon={PlusIcon}
          onClick={onClickCreate}
        >
          New template
        </Button>
      }
    >
      <Stack spacing={4}>
        <DataTableCard>
          <Stack spacing={2}>
            <Typography variant="body-s">Firm templates</Typography>
            <TenantPresentationTemplateTable
              alwaysShowPaginationFooter
              onRowClick={({ id: templateId }) => openEditor(templateId)}
              variant={PresentationTemplateTableVariant.Tenant}
            />
          </Stack>
        </DataTableCard>
        <DataTableCard>
          <Stack spacing={2}>
            <Typography variant="body-s">Luminary templates</Typography>
            <SystemPresentationTemplateTable
              alwaysShowPaginationFooter
              variant={PresentationTemplateTableVariant.Tenant}
              onDuplicateComplete={openEditor}
              onRowClick={({ id: templateId }) =>
                openSystemTemplateAsNew(templateId)
              }
            />
          </Stack>
        </DataTableCard>
      </Stack>
    </SubpageLayout>
  );
}
